/*!
 * TinyPoker 數據分析儀表板
 * 提供營收趨勢、俱樂部表現、玩家活躍度等分析圖表
 */
import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  CardFooter,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Input,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import axios from "axios";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";
import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyPageTabs from "components/tiny/TinyPageTabs";
import TinyLoading from "components/tiny/TinyLoading";
// Note: Chart.js integration would require additional setup
// For now, we'll display data in tables with visual indicators

const API_BASE = "/api/tiny";

function TinyAnalytics() {
  // State
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState(null);
  const [clubsPerformance, setClubsPerformance] = useState(null);
  const [rakeBreakdown, setRakeBreakdown] = useState(null);
  const [topPlayers, setTopPlayers] = useState(null);
  const [weeklyComparison, setWeeklyComparison] = useState(null);
  
  // Filters
  const [selectedWeek, setSelectedWeek] = useState("");
  const [weeks, setWeeks] = useState([]);

  // 載入初始數據
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, trendRes, weeksRes] = await Promise.all([
        axios.get(`${API_BASE}/analytics/overview`),
        axios.get(`${API_BASE}/analytics/revenue-trend?weeks=8`),
        axios.get(`${API_BASE}/reports/weeks`),
      ]);

      if (overviewRes.data.code === 1) {
        setOverview(overviewRes.data.data);
      }
      if (trendRes.data.code === 1) {
        setRevenueTrend(trendRes.data.data);
      }
      if (weeksRes.data.code === 1) {
        const weeksList = weeksRes.data.data.weeks || [];
        setWeeks(weeksList);
        if (weeksList.length > 0) {
          setSelectedWeek(weeksList[0]);
        }
      }
    } catch (err) {
      console.error("載入數據失敗:", err);
      setError("載入數據失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 載入週期相關數據
  useEffect(() => {
    if (selectedWeek) {
      loadWeekData(selectedWeek);
    }
  }, [selectedWeek]);

  const loadWeekData = async (week) => {
    try {
      const [clubsRes, breakdownRes, playersRes, comparisonRes] = await Promise.all([
        axios.get(`${API_BASE}/analytics/clubs-performance?week=${week}&limit=20`),
        axios.get(`${API_BASE}/analytics/rake-breakdown?week=${week}`),
        axios.get(`${API_BASE}/analytics/top-players?week=${week}&metric=rake&limit=15`),
        axios.get(`${API_BASE}/analytics/weekly-comparison`),
      ]);

      if (clubsRes.data.code === 1) {
        setClubsPerformance(clubsRes.data.data);
      }
      if (breakdownRes.data.code === 1) {
        setRakeBreakdown(breakdownRes.data.data);
      }
      if (playersRes.data.code === 1) {
        setTopPlayers(playersRes.data.data);
      }
      if (comparisonRes.data.code === 1) {
        setWeeklyComparison(comparisonRes.data.data);
      }
    } catch (err) {
      console.error("載入週期數據失敗:", err);
    }
  };

  // 格式化金額
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "0.00";
    return value.toLocaleString("zh-TW", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // 格式化百分比
  const formatPercent = (value) => {
    if (value === undefined || value === null) return "0.00%";
    return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
  };

  // 格式化週期顯示
  const formatWeek = (week) => {
    if (!week) return "";
    const parts = week.split("-");
    if (parts.length === 2) {
      return `${parts[0].slice(4, 6)}/${parts[0].slice(6, 8)} - ${parts[1].slice(4, 6)}/${parts[1].slice(6, 8)}`;
    }
    return week;
  };

  // 變化指示器
  const ChangeIndicator = ({ value, suffix = "" }) => {
    if (value === undefined || value === null) return null;
    const isPositive = value > 0;
    const isNegative = value < 0;
    return (
      <span
        className={`ml-2 ${isPositive ? "text-success" : isNegative ? "text-danger" : "text-muted"}`}
      >
        {isPositive ? "▲" : isNegative ? "▼" : "─"}
        {Math.abs(value).toFixed(2)}{suffix}
      </span>
    );
  };

  // 進度條視覺化
  const ProgressBar = ({ value, max, color = "primary" }) => {
    const percent = max > 0 ? (value / max) * 100 : 0;
    return (
      <div className="progress" style={{ height: "8px" }}>
        <div
          className={`progress-bar bg-${color}`}
          role="progressbar"
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="content">
        <TinyLoading variant="page" text="載入中" />
      </div>
    );
  }

  return (
    <div className="content">
      {/* ⚠️ 標題原本用 `fa fa-chart-column` —— 那個 glyph class 在這個
          專案根本沒被定義（nucleo-icons.css 只有 @font-face），所以一直是
          隱形的。公版會自動用 routes.js 的側欄圖示（fa fa-line-chart）。
          ⚠️ 名稱也統一成側欄的「數據分析」（原本寫「數據分析儀表板」）。 */}
      <TinyPageHeader
        title="數據分析"
        description="單週的營收趨勢、俱樂部表現與活躍度。"
        scope={<TinyWeekSelector
              label=""
              options={weeks}
              value={selectedWeek}
              onChange={setSelectedWeek}
              bsSize="sm"
              showMeta={false}
              renderOptionLabel={(week) => formatWeek(week)}
            />}
      />

      {/* 提示訊息 */}
      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 概覽卡片 */}
      <Row>
        <Col lg="3" md="6" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col md="4" xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="fa fa-calendar text-warning" />
                  </div>
                </Col>
                <Col md="8" xs="7">
                  <div className="numbers">
                    <p className="card-category">總週數</p>
                    <CardTitle tag="p">{overview?.total_weeks || 0}</CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <hr />
              <div className="stats">
                <i className="fas fa-sync-alt mr-1" />
                最新週: {formatWeek(overview?.latest_week?.week)}
              </div>
            </CardFooter>
          </Card>
        </Col>
        <Col lg="3" md="6" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col md="4" xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="fa fa-coins text-success" />
                  </div>
                </Col>
                <Col md="8" xs="7">
                  <div className="numbers">
                    <p className="card-category">本週總水</p>
                    <CardTitle tag="p">
                      {formatCurrency(overview?.latest_week?.total_rake)}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <hr />
              <div className="stats">
                <ChangeIndicator
                  value={overview?.trends?.rake_change_pct}
                  suffix="%"
                />
                {" "}較上週
              </div>
            </CardFooter>
          </Card>
        </Col>
        <Col lg="3" md="6" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col md="4" xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="fa fa-building-columns text-danger" />
                  </div>
                </Col>
                <Col md="8" xs="7">
                  <div className="numbers">
                    <p className="card-category">本週淨營收</p>
                    <CardTitle tag="p">
                      {formatCurrency(overview?.latest_week?.net_revenue)}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <hr />
              <div className="stats">
                <i className="fas fa-chart-line mr-1" />
                {overview?.total_clubs} 個俱樂部
              </div>
            </CardFooter>
          </Card>
        </Col>
        <Col lg="3" md="6" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col md="4" xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="fa fa-user text-primary" />
                  </div>
                </Col>
                <Col md="8" xs="7">
                  <div className="numbers">
                    <p className="card-category">活躍會員</p>
                    <CardTitle tag="p">
                      {(overview?.total_members || 0).toLocaleString()}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <hr />
              <div className="stats">
                <i className="fa fa-person-running mr-1" />
                本週活躍
              </div>
            </CardFooter>
          </Card>
        </Col>
      </Row>

      {/* 分頁內容 */}
      <TinyPageTabs
        items={[
          { key: "overview", label: "營收趨勢", icon: "fa fa-line-chart" },
          { key: "clubs", label: "俱樂部表現", icon: "fa fa-building" },
        ]}
        active={activeTab}
        onChange={setActiveTab}
      />

      <TabContent activeTab={activeTab}>
        {/* 營收趨勢 */}
        <TabPane tabId="overview">
          <Row>
            <Col md="12">
              <Card>
                <CardHeader>
                  <CardTitle tag="h5">
                    <i className="fa fa-chart-column mr-2"></i>
                    營收趨勢（近 8 週）
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  {revenueTrend ? (
                    <Table responsive hover>
                      <thead className="text-primary">
                        <tr>
                          <th>週期</th>
                          <th className="text-right">總水</th>
                          <th className="text-right">淨水</th>
                          <th className="text-right">淨營收</th>
                          <th className="text-right">輸退獎池</th>
                          <th className="text-right">活躍獎池</th>
                        </tr>
                      </thead>
                      <tbody>
                        {revenueTrend.weeks.map((week, idx) => (
                          <tr key={week}>
                            <td>{formatWeek(week)}</td>
                            <td className="text-right">
                              {formatCurrency(revenueTrend.total_rake[idx])}
                            </td>
                            <td className="text-right">
                              {formatCurrency(revenueTrend.net_rake[idx])}
                            </td>
                            <td className="text-right">
                              <strong>
                                {formatCurrency(revenueTrend.net_revenue[idx])}
                              </strong>
                            </td>
                            <td className="text-right">
                              {formatCurrency(revenueTrend.bonus_pools.loss_refund[idx])}
                            </td>
                            <td className="text-right">
                              {formatCurrency(revenueTrend.bonus_pools.active[idx])}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <TinyLoading variant="block" text="載入中" />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          
          {/* 週對週比較 */}
          {weeklyComparison && (
            <Row>
              <Col md="12">
                <Card>
                  <CardHeader>
                    <CardTitle tag="h5">
                      <i className="fa fa-rotate-right mr-2"></i>
                      週對週比較
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md="4">
                        <div className="text-center p-3 border rounded">
                          <small className="text-muted">總水變化</small>
                          <h4 className={weeklyComparison.changes.rake_change >= 0 ? "text-success" : "text-danger"}>
                            {formatPercent(weeklyComparison.changes.rake_change_pct)}
                          </h4>
                          <small>
                            {formatCurrency(Math.abs(weeklyComparison.changes.rake_change))}
                          </small>
                        </div>
                      </Col>
                      <Col md="4">
                        <div className="text-center p-3 border rounded">
                          <small className="text-muted">淨營收變化</small>
                          <h4 className={weeklyComparison.changes.net_revenue_change >= 0 ? "text-success" : "text-danger"}>
                            {formatPercent(weeklyComparison.changes.net_revenue_change_pct)}
                          </h4>
                          <small>
                            {formatCurrency(Math.abs(weeklyComparison.changes.net_revenue_change))}
                          </small>
                        </div>
                      </Col>
                      <Col md="4">
                        <div className="text-center p-3 border rounded">
                          <small className="text-muted">會員數變化</small>
                          <h4 className={weeklyComparison.changes.members_change >= 0 ? "text-success" : "text-danger"}>
                            {formatPercent(weeklyComparison.changes.members_change_pct)}
                          </h4>
                          <small>
                            {Math.abs(weeklyComparison.changes.members_change)} 人
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </TabPane>

        {/* 俱樂部表現 */}
        <TabPane tabId="clubs">
          <Card>
            <CardHeader>
              <CardTitle tag="h5">
                <i className="fa fa-shop mr-2"></i>
                俱樂部表現排行（{formatWeek(selectedWeek)}）
              </CardTitle>
            </CardHeader>
            <CardBody>
              {clubsPerformance ? (
                <Table responsive hover>
                  <thead className="text-primary">
                    <tr>
                      <th>#</th>
                      <th>俱樂部</th>
                      <th className="text-right">總水</th>
                      <th style={{ width: "200px" }}>占比</th>
                      <th className="text-right">會員數</th>
                      <th className="text-right">玩家輸贏</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clubsPerformance.clubs.map((club, idx) => {
                      const maxRake = clubsPerformance.clubs[0]?.total_rake || 1;
                      return (
                        <tr key={club.club_id}>
                          <td>{idx + 1}</td>
                          <td>
                            <strong>{club.club_name}</strong>
                            <br />
                            <small className="text-muted">{club.club_id}</small>
                          </td>
                          <td className="text-right">
                            {formatCurrency(club.total_rake)}
                          </td>
                          <td>
                            <ProgressBar
                              value={club.total_rake}
                              max={maxRake}
                              color="success"
                            />
                          </td>
                          <td className="text-right">
                            <Badge color="info">{club.members_count}</Badge>
                          </td>
                          <td className={`text-right ${club.total_pl >= 0 ? "text-success" : "text-danger"}`}>
                            {formatCurrency(club.total_pl)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <TinyLoading variant="block" text="載入週期資料" />
              )}
            </CardBody>
          </Card>
        </TabPane>

        {/* 玩家排行 */}
        <TabPane tabId="players">
          <Card>
            <CardHeader>
              <CardTitle tag="h5">
                <i className="fa fa-user mr-2"></i>
                玩家排行（{formatWeek(selectedWeek)}）
              </CardTitle>
            </CardHeader>
            <CardBody>
              {topPlayers ? (
                <Table responsive hover>
                  <thead className="text-primary">
                    <tr>
                      <th>#</th>
                      <th>玩家</th>
                      <th>俱樂部</th>
                      <th className="text-right">服務費</th>
                      <th className="text-right">手數</th>
                      <th className="text-right">輸贏</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPlayers.players.map((player, idx) => (
                      <tr key={player.member_id}>
                        <td>{idx + 1}</td>
                        <td>
                          <strong>{player.member_name || player.member_id}</strong>
                        </td>
                        <td>
                          <small>{player.club_name}</small>
                        </td>
                        <td className="text-right">
                          {formatCurrency(player.total_rake)}
                        </td>
                        <td className="text-right">
                          {player.total_hands.toLocaleString()}
                        </td>
                        <td className={`text-right ${player.player_pl >= 0 ? "text-success" : "text-danger"}`}>
                          {formatCurrency(player.player_pl)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <TinyLoading variant="block" text="載入週期資料" />
              )}
            </CardBody>
          </Card>
        </TabPane>

        {/* 收入細分 */}
        <TabPane tabId="breakdown">
          <Row>
            <Col md="6">
              <Card>
                <CardHeader>
                  <CardTitle tag="h5">
                    <i className="fa fa-chart-pie mr-2"></i>
                    收入細分（{formatWeek(selectedWeek)}）
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  {rakeBreakdown ? (
                    <>
                      <div className="mb-3">
                        <strong>總水：</strong>
                        <span className="float-right">
                          {formatCurrency(rakeBreakdown.total_rake)}
                        </span>
                      </div>
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>類型</th>
                            <th className="text-right">金額</th>
                            <th className="text-right">占比</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(rakeBreakdown.breakdown).map(([key, value]) => (
                            <tr key={key}>
                              <td>
                                <Badge
                                  color={
                                    key === "ring" ? "success" :
                                    key === "aof" ? "warning" :
                                    key === "mtt" ? "info" : "secondary"
                                  }
                                >
                                  {key.toUpperCase()}
                                </Badge>
                              </td>
                              <td className="text-right">{formatCurrency(value)}</td>
                              <td className="text-right">
                                {rakeBreakdown.percentages[key]?.toFixed(2)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  ) : (
                    <TinyLoading variant="block" text="載入週期資料" />
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md="6">
              <Card>
                <CardHeader>
                  <CardTitle tag="h5">
                    <i className="fa fa-pen-ruler mr-2"></i>
                    收入占比視覺化
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  {rakeBreakdown && (
                    <div>
                      {Object.entries(rakeBreakdown.breakdown)
                        .sort((a, b) => b[1] - a[1])
                        .map(([key, value]) => (
                          <div key={key} className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <span>{key.toUpperCase()}</span>
                              <span>{rakeBreakdown.percentages[key]?.toFixed(1)}%</span>
                            </div>
                            <ProgressBar
                              value={rakeBreakdown.percentages[key] || 0}
                              max={100}
                              color={
                                key === "ring" ? "success" :
                                key === "aof" ? "warning" :
                                key === "mtt" ? "info" : "secondary"
                              }
                            />
                          </div>
                        ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </TabContent>
    </div>
  );
}

export default TinyAnalytics;


